import { Location } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { assignBrowserLocation } from '@core/services/cb2-preview/cb2-preview-navigation.policy';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import {
  isLinkValue,
  resolveLinkValueToModalOpen,
  resolveLinkValueToUrl,
  type LinkValue,
  type ShopRef,
} from '@front-platform/content-builder';

export const locationAssign = new InjectionToken<(url: string) => void>('locationAssign', {
  providedIn: 'root',
  factory: () => {
    const preview = inject(Cb2PreviewStateService);
    return (url: string) => assignBrowserLocation(url, preview.isActive());
  },
});

import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ShopKeys } from '@config/keys/shop.keys';
import { VenueAddressKeys, VenueKeys } from '@config/keys/venue.keys';
import { DoodShopModel } from '@core/models/shop.model';
import { navigateToShopPage } from '@core/services/navigate/navigate-to-shop-page';
import { ModalsService } from '@core/services/modals/modals.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { SlugUtils } from '@shared/utils/slug/slug.utils';

export function toShopRef(shop: DoodShopModel | null): ShopRef | null {
  const shopId = shop?.[ShopKeys.Id];
  const shopCity = shop?.[ShopKeys.Venue]?.[VenueKeys.Address]?.[VenueAddressKeys.City];
  const shopType = shop?.[ShopKeys.Type]?.name;
  const shopSlug = shop?.[ShopKeys.Slug];

  if (!shopId || !shopCity || !shopType || !shopSlug) {
    return null;
  }

  return {
    shopId,
    shopCity: SlugUtils.formatSlug(shopCity),
    shopType: SlugUtils.formatSlug(shopType),
    shopSlug,
    ...(shop[ShopKeys.Name] ? { shopName: shop[ShopKeys.Name] } : {}),
  };
}

const LinkNavigationType = {
  Relative: 'relative',
  Mailto: 'mailto',
  Modal: 'modal',
  Anchor: 'anchor',
  External: 'external',
  ActiveShop: 'activeShop',
  Internal: 'internal',
} as const;

type LinkNavigationType = (typeof LinkNavigationType)[keyof typeof LinkNavigationType];

@Injectable({ providedIn: 'root' })
export class LinkNavigationService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly modalsService = inject(ModalsService);
  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly navigateToShopPage = inject(navigateToShopPage);
  private readonly assignLocation = inject(locationAssign);

  navigate(link?: string | LinkValue): void {
    if (link == null) {
      return;
    }

    if (isLinkValue(link)) {
      if (link.kind === 'modal') {
        const open = resolveLinkValueToModalOpen(link);
        if (!open) {
          return;
        }
        this.modalsService.open(open.handle as never, undefined, open.data as never);
        return;
      }
      const resolvedUrl = resolveLinkValueToUrl(link, {
        resolveActiveShop: () => toShopRef(this.shopSelector.selectActive()),
      });
      const url = link.kind === 'page' && !resolvedUrl ? '/' : resolvedUrl;
      if (!url) {
        return;
      }
      this.navigate(url);
      return;
    }

    if (!link.trim()) {
      return;
    }

    switch (this.resolveLinkType(link)) {
      case LinkNavigationType.Relative:
        if (link.includes('navigationBack')) {
          this.location.back();
        }
        break;

      case LinkNavigationType.Mailto:
      case LinkNavigationType.External:
        this.assignLocation(link);
        break;

      case LinkNavigationType.Modal:
        this.modalsService.openFromLink(link);
        break;

      case LinkNavigationType.Anchor:
        this.router.navigate([], { fragment: link.substring(1) });
        break;

      case LinkNavigationType.ActiveShop:
        this.navigateActiveShopLink(link);
        break;

      case LinkNavigationType.Internal:
        this.navigateInternalRoute(link);
        break;
    }
  }

  private resolveLinkType(link: string): LinkNavigationType {
    if (this.routerHelper.isRelativeMode(link)) {
      return LinkNavigationType.Relative;
    }
    if (this.routerHelper.isMailto(link)) {
      return LinkNavigationType.Mailto;
    }
    if (this.modalsService.isModalLink(link)) {
      return LinkNavigationType.Modal;
    }
    if (this.routerHelper.isAnchor(link)) {
      return LinkNavigationType.Anchor;
    }
    if (this.routerHelper.isLinkExternal(link)) {
      return LinkNavigationType.External;
    }
    if (link.includes('{{activeShop}}')) {
      return LinkNavigationType.ActiveShop;
    }
    return LinkNavigationType.Internal;
  }

  private navigateActiveShopLink(link: string): void {
    const activeShop = this.shopSelector.selectActive();
    if (activeShop) {
      const extraUrl = link.split('{{activeShop}}')[1]?.replace(/^\//, '').trim();
      this.navigateToShopPage(activeShop, { extraUrl: extraUrl || undefined });
      return;
    }
    this.location.back();
  }

  private navigateInternalRoute(link: string): void {
    // Split path and query string so that query params (e.g. ?catalogId=...) are
    // forwarded as queryParams to the router rather than embedded in the URL path.
    const queryIndex = link.indexOf('?');
    const path = queryIndex >= 0 ? link.slice(0, queryIndex) : link;
    const queryString = queryIndex >= 0 ? link.slice(queryIndex + 1) : undefined;
    const translatedPath = this.routerHelper.translateRoute(path);
    if (queryString) {
      const queryParams: Record<string, string> = {};
      new URLSearchParams(queryString).forEach((value, key) => {
        queryParams[key] = value;
      });
      this.router.navigate([translatedPath], { queryParams });
      return;
    }
    this.router.navigate([translatedPath]);
  }
}
